import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import BadgeCelebration from '@/components/BadgeCelebration';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const { checkAuth, user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying');
  const [showCelebration, setShowCelebration] = useState(false);
  const [updatedUser, setUpdatedUser] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      navigate('/support');
      return;
    }

    const verifyPayment = async () => {
      console.log('[PaymentSuccess] Starting verification for session:', sessionId);
      
      try {
        // Poll for payment confirmation with exponential backoff
        let attempts = 0;
        const maxAttempts = 8; // Reduced from 15 to 8
        
        while (attempts < maxAttempts) {
          attempts++;
          
          // Exponential backoff: 2s, 3s, 4.5s, 6.75s, 10s, 10s, 10s, 10s
          const delay = Math.min(2000 * Math.pow(1.5, attempts - 1), 10000);
          console.log(`[PaymentSuccess] Polling attempt ${attempts}/${maxAttempts} (delay: ${delay}ms)`);
          
          try {
            const { data } = await api.get(`/api/payments/status/${sessionId}`);
            console.log('[PaymentSuccess] Status response:', data);
            
            if (data.payment_status === 'paid') {
              console.log('[PaymentSuccess] Payment confirmed! Refreshing user data...');
              setStatus('success');
              
              // Refresh user data to update subscription status and badge
              await checkAuth();
              
              // Get fresh user data with badge
              const { data: userData } = await api.get('/api/auth/me');
              console.log('[PaymentSuccess] User data refreshed:', userData.subscription_status);
              setUpdatedUser(userData);
              
              // Show celebration
              if (userData?.badge) {
                console.log('[PaymentSuccess] Showing badge celebration:', userData.badge.name);
                setShowCelebration(true);
              }
              break;
            }
            
            console.log('[PaymentSuccess] Payment still pending, retrying...');
            await new Promise(resolve => setTimeout(resolve, delay));
            
          } catch (pollError) {
            console.error('[PaymentSuccess] Poll attempt failed:', pollError);
            
            // If it's a 404, the backend will create the record on the fly
            // Keep retrying in case backend is still processing
            if (attempts < maxAttempts) {
              console.log('[PaymentSuccess] Retrying after error...');
              await new Promise(resolve => setTimeout(resolve, delay));
              continue;
            }
            throw pollError;
          }
        }
        
        // After all attempts, refresh user data anyway
        console.log('[PaymentSuccess] Polling complete, refreshing user data...');
        setStatus('success');
        await checkAuth();
        const { data: userData } = await api.get('/api/auth/me');
        console.log('[PaymentSuccess] Final user data:', userData.subscription_status);
        setUpdatedUser(userData);
        if (userData?.badge) {
          setShowCelebration(true);
        }
        
      } catch (error) {
        console.error('[PaymentSuccess] Payment verification error:', error);
        // Still show success page, but log the error for debugging
        setStatus('success');
        
        // Try to refresh user data at least
        try {
          await checkAuth();
          const { data: userData } = await api.get('/api/auth/me');
          setUpdatedUser(userData);
        } catch (refreshError) {
          console.error('[PaymentSuccess] Failed to refresh user data:', refreshError);
        }
      }
    };

    verifyPayment();
  }, [sessionId, navigate, checkAuth]);

  const handleCloseCelebration = () => {
    setShowCelebration(false);
  };

  const handleManualSync = async () => {
    console.log('[PaymentSuccess] Manual sync requested');
    try {
      const { data } = await api.post(`/api/payments/sync-session/${sessionId}`);
      console.log('[PaymentSuccess] Manual sync result:', data);
      
      if (data.success) {
        // Refresh user data
        await checkAuth();
        const { data: userData } = await api.get('/api/auth/me');
        setUpdatedUser(userData);
        
        if (userData?.badge) {
          setShowCelebration(true);
        }
        
        alert('✅ Payment synced! Your subscription is now active. Refresh the page to see your Diamond badge.');
      }
    } catch (error) {
      console.error('[PaymentSuccess] Manual sync error:', error);
      alert('❌ Sync failed. Please contact support with your payment confirmation.');
    }
  };

  return (
    <>
      <BadgeCelebration
        badge={updatedUser?.badge || user?.badge}
        show={showCelebration}
        onClose={handleCloseCelebration}
      />
      
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            {status === 'verifying' ? (
              <>
                <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin text-primary" />
                <CardTitle>Verifying Payment...</CardTitle>
              </>
            ) : (
              <>
                <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-600" />
                <CardTitle>Payment Successful!</CardTitle>
              </>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {status === 'success' && (
              <>
                <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                  <p className="text-sm text-green-700 dark:text-green-300 text-center">
                    Thank you for your support! Your contribution helps us keep building amazing features.
                  </p>
                  {updatedUser?.badge && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-2 text-center font-medium">
                      🎉 You&apos;ve earned the {updatedUser.badge.name} badge!
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Button onClick={() => navigate('/dashboard')} className="w-full">
                    Continue to Dashboard
                  </Button>
                  
                  <Button 
                    onClick={handleManualSync} 
                    variant="outline" 
                    className="w-full text-sm"
                  >
                    🔄 Didn&apos;t get your membership? Click to sync payment
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
